import { useNavigate } from '@tanstack/react-router'
import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { Input } from '@workspace/ui/components/input'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@workspace/ui/components/tooltip'
import { cn } from '@workspace/ui/lib/utils'
import {
  ArrowUpRight,
  Bot,
  ChevronDown,
  CircleCheck,
  Ellipsis,
  Eye,
  FileText,
  Info,
  Pencil,
  Search,
  Share2,
  X,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useInvestorContext } from '../../../../contexts/investor-guard'
import { useBasePath } from '../../../../hooks/use-base-path'
import { trackEvent } from '../../../../lib/gtag'
import { copy } from '../../copy/investor-copy'
import {
  useEditFollow,
  useFollowsQuery,
  useNotificationsQuery,
  useShareStartupMutation,
} from '../../hooks/use-investor-assistant'
import { ActionMenu } from '../action-menu'
import { FilterChip } from '../filter-chip'
import { FollowWindow } from '../relationship/follow-window'
import { StopDialog } from '../relationship/stop-dialog'
import { type ShareDraft, ShareWindow } from '../search/share-window'
import { FounderUpdates } from './founder-updates'
import { roundLabel } from './round'
import { compareBy, type SortKey } from './sort'
import { StartupTile } from './startup-tile'

type Follow = NonNullable<ReturnType<typeof useFollowsQuery>['data']>['data'][number]

const WEEK_MS = 7 * 24 * 60 * 60 * 1000

/** The first click on a column sorts the way that column reads best; the next click flips it. */
const DEFAULT_DESC: Record<SortKey, boolean> = {
  added: true,
  name: false,
  round: true,
  updates: true,
  status: false,
}

/** A column header that sorts the table; the arrow shows the direction it is sorted in. */
function SortableHeader({
  label,
  sortKey,
  sort,
  onSort,
  className,
}: {
  label: string
  sortKey: SortKey
  sort: { key: SortKey; desc: boolean }
  onSort: (key: SortKey) => void
  className?: string
}) {
  const active = sort.key === sortKey
  const direction = active ? (sort.desc ? 'descending' : 'ascending') : 'none'
  return (
    <th
      aria-sort={direction}
      className={cn(
        'h-10 px-3 text-left align-middle text-[11px] font-medium uppercase tracking-wider text-muted-foreground',
        className
      )}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        aria-label={`Sort by ${label.toLowerCase()}`}
        className={cn(
          'inline-flex items-center gap-1 uppercase tracking-wider',
          active ? 'text-foreground' : 'hover:text-foreground'
        )}
      >
        {label}
        <ChevronDown
          className={cn(
            'size-3.5 transition-transform',
            !active && 'opacity-0',
            active && !sort.desc && 'rotate-180'
          )}
        />
      </button>
    </th>
  )
}

type Filter = 'all' | 'new' | 'met' | 'raising' | 'notes'

function followingFor(follow: Follow): string {
  if (follow.mode === 'ALL_UPDATES') return 'Material updates'
  return follow.conditions.find((c) => c.key === 'watch')?.text ?? 'A condition you named'
}

/** Why the startup is in the list: what the investor asked to hear about, in one sentence. */
function followingWhy(follow: Follow): string {
  return follow.mode === 'ALL_UPDATES' ? copy('row.why.material') : copy('row.why.condition')
}

function StatusCell({ follow, basePath }: { follow: Follow; basePath: string }) {
  switch (follow.state) {
    case 'CONDITION_MET':
      return (
        <>
          <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-400">
            <CircleCheck className="size-3.5" />
            {copy('card.following.chip.met')}
          </span>
          <span className="mt-0.5 block text-xs text-muted-foreground">
            {copy('card.following.materialNow')}
          </span>
        </>
      )
    case 'WAITING_CHECK':
      return <span className="text-xs text-amber-400">{copy('row.waitingCheck')}</span>
    case 'WAITING_PLACE':
      return (
        // Free places are the plan's: the wallet is where to get more.
        <a
          href={`${basePath}/wallet`}
          title={copy('row.waitingPlace.title')}
          onClick={(e) => e.stopPropagation()}
          className="text-xs font-medium text-amber-400 hover:underline"
        >
          {copy('row.waitingPlace')}
        </a>
      )
    case 'ENDED_BY_FOUNDER':
      return <span className="text-xs text-muted-foreground">{copy('row.endedByFounder')}</span>
    default:
      return <span className="text-xs text-muted-foreground">{copy('row.active')}</span>
  }
}

function WatchlistRow({
  follow,
  hasNote,
  unread,
  selected,
  panelOpen,
  onOpen,
}: {
  follow: Follow
  hasNote: boolean
  unread: boolean
  selected: boolean
  /** While the panel is open its own ⋯ menu carries the actions, so the row has none. */
  panelOpen: boolean
  onOpen: (startupId: string, writeNote?: boolean) => void
}) {
  const navigate = useNavigate()
  const basePath = useBasePath()
  const { investor } = useInvestorContext()
  const [dialog, setDialog] = useState<'stop' | 'edit' | null>(null)
  const [shareDraft, setShareDraft] = useState<ShareDraft | null>(null)
  const share = useShareStartupMutation()
  const menuButton = useRef<HTMLButtonElement>(null)
  const token = follow.profileLinkToken
  const removed = follow.state === 'ENDED_BY_FOUNDER'
  const waiting = follow.state === 'WAITING_CHECK' || follow.state === 'WAITING_PLACE'
  const openProfile = () => {
    if (!token) return
    trackEvent('assistant_startup_link_clicked', {
      surface: 'follows',
      startup_id: follow.startupId,
    })
    navigate({ to: '/app/investor/$id/startup/$token', params: { id: investor.id, token } })
  }
  const open = () => onOpen(follow.startupId)

  return (
    <tr
      data-startup-id={follow.startupId}
      onClick={(e) => {
        // The menu and its dialog render in a portal but still bubble here through React: only a
        // click that lands on the row itself, and not on one of its controls, opens the panel.
        const target = e.target as HTMLElement
        if (!e.currentTarget.contains(target) || target.closest('button, a')) return
        open()
      }}
      className={cn(
        'cursor-pointer border-t border-border hover:bg-white/[0.04]',
        selected && 'bg-white/5',
        follow.state === 'CONDITION_MET' && 'sf-r-met',
        removed && 'opacity-60'
      )}
    >
      <td className="sf-c-name py-3 pl-5 pr-3">
        <div className="flex items-center gap-3">
          <StartupTile name={follow.startupName} muted={removed} />
          <div className="min-w-0 flex-1">
            <div className="flex min-w-[8rem] items-center gap-x-1.5">
              <a
                href={`?open=${follow.startupId}`}
                onClick={(e) => {
                  // A real link for keyboard and middle-click; a plain click opens the panel.
                  e.preventDefault()
                  open()
                }}
                className="min-w-0 truncate text-sm font-semibold text-foreground hover:underline"
              >
                {follow.startupName}
              </a>
              {follow.freeThroughInvite && (
                <span className="whitespace-nowrap text-xs text-muted-foreground">
                  {copy('row.freeInvite')}
                </span>
              )}
              {follow.createdVia === 'AGENT' && (
                // Only the icon in a narrow table (see index.css); the words stay in the tooltip.
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="flex shrink-0 items-center gap-1 whitespace-nowrap text-xs text-muted-foreground">
                        <Bot className="size-3.5" />
                        <span className="sf-badge-text">{copy('row.throughAi')}</span>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="rounded-md border border-border bg-input px-3 py-2 text-xs text-foreground shadow-md">
                      {copy('row.throughAi')}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            {follow.description && (
              <p className="truncate text-xs text-muted-foreground">{follow.description}</p>
            )}
          </div>
        </div>
      </td>
      <td className="sf-col-round whitespace-nowrap px-3 py-3 text-sm tabular-nums text-foreground/80">
        {roundLabel(follow.round)}
      </td>
      <td className="sf-col-follow px-3 py-3">
        <FollowingForCell text={followingFor(follow)} why={followingWhy(follow)} />
      </td>
      <td className="sf-col-upd sf-c-upd px-3 py-3">
        <FounderUpdates
          weekly={follow.founderUpdates.weekly}
          latestAt={follow.founderUpdates.latestAt}
          followedAt={follow.followedAt}
          unread={unread}
        />
      </td>
      <td className="sf-c-status px-3 py-3">
        <StatusCell follow={follow} basePath={basePath} />
      </td>
      <td className="sf-c-act px-3 py-3 text-right">
        <div className="flex items-center justify-end gap-1">
          {/* A fixed slot for the markers, so the menu buttons line up whether or not a row has them. */}
          <span className="inline-flex w-12 shrink-0 items-center justify-end gap-2">
            {hasNote && (
              <FileText
                role="img"
                aria-label={copy('note.title')}
                className="size-4 text-muted-foreground"
              >
                <title>{copy('note.title')}</title>
              </FileText>
            )}
            {follow.hasAssessment && (
              <Bot
                role="img"
                aria-label={copy('ai.title')}
                className="size-4 text-muted-foreground"
              >
                <title>{copy('ai.title')}</title>
              </Bot>
            )}
          </span>
          {!panelOpen && (
            <ActionMenu
              label={copy('panel.aria.more', { startup: follow.startupName })}
              actions={[
                ...(token
                  ? [
                      {
                        key: 'open',
                        icon: ArrowUpRight,
                        title: copy('menu.openProfile'),
                        onSelect: openProfile,
                      },
                    ]
                  : []),
                ...(removed
                  ? []
                  : [
                      {
                        key: 'edit',
                        icon: Pencil,
                        title: copy('menu.editFollow'),
                        onSelect: () => setDialog('edit'),
                      },
                      // Same as the panel's menu: the note is written in the panel.
                      {
                        key: 'note',
                        icon: FileText,
                        title: copy('menu.writeNote'),
                        onSelect: () => onOpen(follow.startupId, true),
                      },
                      {
                        key: 'stop',
                        icon: X,
                        title: copy('menu.stopFollowing'),
                        destructive: true,
                        onSelect: () => setDialog('stop'),
                      },
                    ]),
                ...(follow.shareable
                  ? [
                      {
                        key: 'share',
                        icon: Share2,
                        title: copy('search.result.share'),
                        onSelect: () =>
                          share.mutate(
                            { path: { startupId: follow.startupId } },
                            { onSuccess: (response) => setShareDraft(response.data) }
                          ),
                      },
                    ]
                  : []),
              ]}
              trigger={
                <button
                  ref={menuButton}
                  type="button"
                  aria-label={copy('panel.aria.more', { startup: follow.startupName })}
                  className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-white/[0.06] hover:text-foreground"
                >
                  <Ellipsis className="size-4" />
                </button>
              }
            />
          )}
        </div>
        {dialog === 'stop' && (
          <StopDialog
            startupId={follow.startupId}
            startupName={follow.startupName}
            waiting={waiting}
            onClose={() => setDialog(null)}
          />
        )}
        {dialog === 'edit' && (
          <EditFromRow
            follow={follow}
            anchor={menuButton.current}
            onClose={() => setDialog(null)}
          />
        )}
        <ShareWindow draft={shareDraft} onClose={() => setShareDraft(null)} />
      </td>
    </tr>
  )
}

/** The follow's words, cut to the cell; the full text in the dark tooltip. */
/** What the follow is for; on hover, why the startup is in the list. */
function FollowingForCell({ text, why }: { text: string; why: string }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="block max-w-full cursor-default truncate text-xs text-foreground/80">
            {text}
          </span>
        </TooltipTrigger>
        <TooltipContent className="w-72 rounded-md border border-border bg-input px-3 py-2 text-xs leading-relaxed text-foreground shadow-md">
          {why}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

/**
 * The prototype's empty watchlist: what the list is for, and the way to fill it. A filter that
 * matches nothing gets the same card with its own words.
 */
function EmptyWatchlist({
  heading = 'Your watchlist is empty',
  body = 'Follow a startup from its profile. SeedForge tells you when it shares material news, or only when it meets a condition you set.',
  className,
}: {
  heading?: string
  body?: string
  className?: string
}) {
  const navigate = useNavigate()
  const { investor } = useInvestorContext()
  return (
    <section
      className={cn('rounded-xl border border-border bg-card/30 px-8 py-12 text-center', className)}
    >
      <span className="mx-auto flex size-10 items-center justify-center rounded-full bg-elevated text-muted-foreground">
        <Eye className="size-4" />
      </span>
      <h2 className="mt-4 text-base font-semibold text-foreground">{heading}</h2>
      <p className="mx-auto mt-1.5 max-w-md text-sm text-muted-foreground">{body}</p>
      <div className="mt-5 flex justify-center">
        <CtaButton
          size="md"
          onClick={() => navigate({ to: '/app/investor/$id/search', params: { id: investor.id } })}
        >
          Find startups
        </CtaButton>
      </div>
    </section>
  )
}

function EditFromRow({
  follow,
  anchor,
  onClose,
}: {
  follow: Follow
  anchor: Element | null
  onClose: () => void
}) {
  const edit = useEditFollow()
  return (
    <FollowWindow
      open
      mode="edit"
      anchor={anchor}
      startupName={follow.startupName}
      initialCondition={follow.conditions.find((c) => c.key === 'watch')?.text ?? null}
      pending={edit.isPending}
      onOpenChange={(open) => !open && onClose()}
      onSubmit={(condition) =>
        edit.mutate(
          { path: { startupId: follow.startupId }, body: { condition } },
          { onSuccess: onClose }
        )
      }
    />
  )
}

export function WatchlistTab({
  openId,
  onOpen,
}: {
  openId: string | null
  onOpen: (startupId: string, writeNote?: boolean) => void
}) {
  const follows = useFollowsQuery()
  const [filter, setFilter] = useState<Filter>('all')
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({ key: 'added', desc: true })
  const chooseSort = (key: SortKey) =>
    setSort((current) =>
      current.key === key ? { key, desc: !current.desc } : { key, desc: DEFAULT_DESC[key] }
    )
  const inbox = useNotificationsQuery()

  const all = (follows.data?.data ?? []).filter((f) => f.status !== 'ENDED_BY_INVESTOR')
  const isNew = (f: Follow) => Date.now() - new Date(f.followedAt).getTime() < WEEK_MS
  const matches: Record<Filter, (f: Follow) => boolean> = {
    all: () => true,
    new: isNew,
    met: (f) => f.state === 'CONDITION_MET',
    raising: (f) => f.round.isFundraising,
    notes: (f) => f.hasNote,
  }
  const unreadStartups = new Set(
    (inbox.data?.data.items ?? []).filter((n) => !n.readAt && n.startupId).map((n) => n.startupId)
  )
  const shown = all
    .filter(matches[filter])
    .filter((f) => f.startupName.toLowerCase().includes(query.trim().toLowerCase()))
    .sort((a, b) => {
      const direction = sort.desc ? -1 : 1
      const order = compareBy(sort.key, a, b)
      // Ties keep the newest follow first, whatever the column.
      return (
        direction * order || new Date(b.followedAt).getTime() - new Date(a.followedAt).getTime()
      )
    })

  // With the panel open, the arrows move through the list and the panel follows the row.
  useEffect(() => {
    if (openId === null) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return
      const target = event.target as HTMLElement | null
      if (target?.closest('input, textarea, select, [contenteditable="true"], [role="listbox"]')) {
        return
      }
      if (document.querySelector('[role="dialog"][data-state="open"], [role="menu"]')) return
      const index = shown.findIndex((f) => f.startupId === openId)
      const next = shown[index + (event.key === 'ArrowDown' ? 1 : -1)]
      if (index < 0 || !next) return
      event.preventDefault()
      onOpen(next.startupId)
      document
        .querySelector(`tr[data-startup-id="${next.startupId}"]`)
        ?.scrollIntoView({ block: 'nearest' })
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [openId, shown, onOpen])

  return (
    <div className="space-y-8">
      {all.length === 0 ? (
        <EmptyWatchlist />
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-1">
              {(
                [
                  ['all', 'All'],
                  ['new', 'Added this week'],
                  ['met', 'Condition met'],
                  ['raising', 'Raising'],
                  ['notes', 'With notes'],
                ] as const
              ).map(([key, label]) => (
                <FilterChip
                  key={key}
                  label={label}
                  count={all.filter(matches[key]).length}
                  active={filter === key}
                  onClick={() => setFilter(key)}
                />
              ))}
            </div>
            <div className="relative w-56">
              <Search className="absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Find a startup"
                aria-label="Find a startup"
                className="h-8 rounded-full border-border bg-input pl-8 text-xs"
              />
            </div>
          </div>

          <div
            className={cn(
              'sf-cq overflow-hidden rounded-xl border border-border bg-card/30',
              openId !== null && 'sf-panel-open'
            )}
          >
            <table className="w-full table-fixed text-left text-sm">
              <colgroup>
                <col />
                <col className="sf-col-round w-32" />
                <col className="sf-col-follow w-56" />
                <col className="sf-col-upd w-56" />
                <col className="w-44" />
                <col className="w-28" />
              </colgroup>
              <thead>
                <tr>
                  <SortableHeader
                    className="pr-3 pl-5"
                    label="Startup"
                    sortKey="name"
                    sort={sort}
                    onSort={chooseSort}
                  />
                  <SortableHeader
                    className="sf-col-round"
                    label="Round"
                    sortKey="round"
                    sort={sort}
                    onSort={chooseSort}
                  />
                  <th className="sf-col-follow h-10 px-3 text-left align-middle text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex cursor-default items-center gap-1.5">
                            Following for
                            <Info aria-label="What this means" className="size-3.5" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="w-72 rounded-md border border-border bg-input px-3 py-2 text-xs normal-case leading-relaxed tracking-normal text-foreground shadow-md">
                          Alerts for this startup: every material update (new funding, key hires, a
                          pivot or a big milestone), or the condition you set.
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </th>
                  <SortableHeader
                    className="sf-col-upd"
                    label="Founder updates"
                    sortKey="updates"
                    sort={sort}
                    onSort={chooseSort}
                  />
                  <SortableHeader label="Status" sortKey="status" sort={sort} onSort={chooseSort} />
                  <th className="h-10 px-3 text-left align-middle text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {shown.map((follow) => (
                  <WatchlistRow
                    key={follow.observerId}
                    follow={follow}
                    hasNote={follow.hasNote}
                    selected={openId === follow.startupId}
                    panelOpen={openId !== null}
                    onOpen={onOpen}
                    unread={unreadStartups.has(follow.startupId)}
                  />
                ))}
              </tbody>
            </table>
            {shown.length === 0 && (
              <EmptyWatchlist
                className="rounded-none border-0 bg-transparent"
                heading="No startup matches"
                body="Clear the filter or the search, or find more startups on Search."
              />
            )}
          </div>
        </div>
      )}
    </div>
  )
}
